import * as Sentry from '@sentry/nextjs';
import SubmissionStatus from '../../../Data/SubmissionStatus';
import { incrementMetric } from '../../../Services/Datadog';
import { updateMaskedValue, updateValue } from '../slices/components';
import {
  setSubmissionStatus,
  updateSelectAndConnectBrandSubmissionStatus,
} from '../slices/submission';

/**
 * Form whose Select & Connect children carry PII that differs from the parent.
 * The tracer is scoped solely to this form — see the investigation plan.
 */
const TRACED_FORM_ID = 7798;

/**
 * The four PII question keys (admiral ids) that are observed to differ between
 * a Select & Connect parent and child. Never emit their values.
 */
const PII_QUESTION_IDS = [
  'email1',
  'first_name',
  'phone_work',
  'primary_address_postalcode',
];

/**
 * Returns the traced PII question key for a componentId, or null when the
 * componentId is not one of the four PII components. Compared as strings to
 * avoid number/string mismatches between the map and dispatch payloads.
 */
const getPiiQuestionId = ({ componentId, mappedQuestionIdToComponentId }) => {
  if (!mappedQuestionIdToComponentId) return null;

  return (
    PII_QUESTION_IDS.find(
      (questionId) =>
        String(mappedQuestionIdToComponentId[questionId]) ===
        String(componentId)
    ) || null
  );
};

/**
 * Redux middleware that traces writes to the four PII components on form 7798,
 * recording each write's source, timing relative to submission, and whether the
 * value changed — never the values themselves. Instrumentation only; it never
 * alters the action and always forwards it to the next middleware.
 */
const piiTraceMiddleware = (store) => {
  let submissionCompletedAt = null;
  let lastChildSubmitAt = null;

  return (next) => (action) => {
    const state = store.getState();

    if (state.formConfig?.formId !== TRACED_FORM_ID) {
      return next(action);
    }

    if (
      setSubmissionStatus.match(action) &&
      action.payload?.submissionStatus === SubmissionStatus.COMPLETE
    ) {
      submissionCompletedAt = Date.now();
      return next(action);
    }

    if (
      updateSelectAndConnectBrandSubmissionStatus.match(action) &&
      action.payload?.submissionStatus === SubmissionStatus.IN_PROGRESS
    ) {
      lastChildSubmitAt = Date.now();
      return next(action);
    }

    const isValueWrite =
      updateValue.match(action) || updateMaskedValue.match(action);
    if (!isValueWrite) {
      return next(action);
    }

    const { componentId } = action.payload;
    const admiralId = getPiiQuestionId({
      componentId,
      mappedQuestionIdToComponentId:
        state.dataMappings?.mappedQuestionIdToComponentId,
    });

    if (!admiralId) {
      return next(action);
    }

    const isMasked = updateMaskedValue.match(action);
    const component = state.components?.[`component_${componentId}`];
    const previousValue = isMasked
      ? component?.input?.maskedValue
      : component?.input?.value;
    const nextValue = isMasked
      ? action.payload.maskedValue
      : action.payload.value;
    const changed = previousValue !== nextValue;

    const source = action.payload?.meta?.source || 'other';
    const isPostSubmit =
      submissionCompletedAt !== null ||
      state.submission?.submissionStatus === SubmissionStatus.COMPLETE;
    const phase = isPostSubmit ? 'postSubmit' : 'preSubmit';
    const now = Date.now();
    const msSinceSubmitComplete =
      submissionCompletedAt === null ? null : now - submissionCompletedAt;
    const msSinceChildSubmit =
      lastChildSubmitAt === null ? null : now - lastChildSubmitAt;

    Sentry.addBreadcrumb({
      category: 'pii-trace',
      level: 'info',
      message: `pii-write:${source}`,
      data: {
        admiralId,
        componentId,
        source,
        changed,
        phase,
        msSinceSubmitComplete,
        msSinceChildSubmit,
      },
    });

    if (isPostSubmit && changed) {
      const childSid = state.submission?.id;

      Sentry.withScope((scope) => {
        scope.setTag('pii_post_submit_overwrite', true);
        scope.setTag('pii_overwrite_source', source);
        scope.setTag('pii_overwrite_field', admiralId);
        if (childSid) {
          scope.setTag('pii_child_sid', String(childSid));
        }
        Sentry.captureMessage('pii_overwrite_after_submit', 'warning');
      });

      incrementMetric({
        name: 'pii_post_submit_overwrite',
        tags: {
          source,
          admiral_id: admiralId,
          form_id: TRACED_FORM_ID,
        },
      });
    }

    return next(action);
  };
};

export default piiTraceMiddleware;
